import React, { useEffect, useState } from "react";
import classes from "./Overture.module.css";
import SnackBar from "components/SnackBar/SnackBar";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import BackdropComponent from "../../Spinner/Spinner";
import SwapHorizontalCircleOutlinedIcon from "@mui/icons-material/SwapHorizontalCircleOutlined";
import ArrowCircleRightOutlinedIcon from "@mui/icons-material/ArrowCircleRightOutlined";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "components/AccordionComponents/AccordionComponents";
import { CustomTooltip } from "../../../components/RosterConfig/RosterConfigStyle";
import { GridSearchIcon } from "@mui/x-data-grid";
import styled from "@mui/material/styles/styled";
import { DataGrid } from "@mui/x-data-grid";
import applicationUsageLog from "../../../utils/applicationUsagesLog";
import {
  Grid,
  Autocomplete,
  TextField,
  Card,
  Box,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TextLabel from "components/Common/TextLabel/TextLabel";

import {
  getClassRosters,
  getAllParentClients,
  getAllProjectsByParentClientCode,
  getWaveDetails,
  getDistinctTechTypes,
  getOvertureData,
} from "../../../services/userServices";

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

const CustomNoRowsOverlay = () => (
  <div className={classes.noRecordsOverlay}>
    <h3>No results to display.</h3>
  </div>
);

const columns = [
  {
    field: "OvertureResultId",
    headerName: "Overture Id",
    headerClassName: classes.headerClass,
    headerAlign: "center",
    type: "number",
    hideable: false,
    sortable: true,
    editable: false,
    align: "center",
    flex: 1,
  },
  {
    field: "classrosterid",
    headerName: "Roster Id",
    headerClassName: classes.headerClass,
    headerAlign: "center",
    type: "number",
    hideable: false,
    sortable: true,
    editable: false,
    align: "center",
    flex: 1,
  },
  {
    field: "OracleID",
    headerName: "Oracle Id",
    headerClassName: classes.headerClass,
    headerAlign: "center",
    type: "number",
    hideable: false,
    sortable: true,
    editable: false,
    align: "center",
    flex: 1,
  },
  {
    field: "EmployeeName",
    headerName: "Trainee Name",
    headerClassName: classes.headerClass,
    headerAlign: "center",
    type: "string",
    hideable: false,
    sortable: true,
    editable: false,
    align: "center",
    width: 200,
  },
  {
    field: "StartDate",
    headerName: "Start Date",
    headerClassName: classes.headerClass,
    headerAlign: "center",
    type: "string",
    hideable: false,
    sortable: true,
    editable: false,
    align: "center",
    width: 200,
  },
  {
    field: "HostName",
    headerName: "Host Name",
    headerClassName: classes.headerClass,
    headerAlign: "center",
    type: "string",
    hideable: false,
    sortable: true,
    editable: false,
    align: "center",
    width: 200,
  },
  {
    field: "TechType",
    headerName: "Tech Type",
    headerClassName: classes.headerClass,
    headerAlign: "center",
    type: "string",
    hideable: false,
    sortable: true,
    editable: false,
    align: "center",
  },
  {
    field: "Status",
    headerName: "Status",
    headerClassName: classes.headerClass,
    headerAlign: "center",
    type: "string",
    hideable: false,
    sortable: true,
    editable: false,
    align: "center",
  },
  {
    field: "ErrorsEncountered",
    headerName: "Errors Encountered",
    headerClassName: classes.headerClass,
    headerAlign: "center",
    type: "string",
    hideable: false,
    sortable: true,
    editable: false,
    align: "center",
    width: 400,
  },
];

export default function Overture() {
  const defaultTheme = createTheme({
    palette: {
      primary: {
        main: "#3299FF",
      },
    },
  });

  const [rows, setRows] = useState([]);
  const [agentSearchValue, setAgentSearchValue] = useState("");

  const [expanded, setExpanded] = useState("panel1");
  const [loading, setLoading] = useState(false);
  const [filterRosterData, setFilterRosterData] = useState([]);
  const [isMaxRoster, setIsMaxRoster] = useState(false);
  const [rosterValue, setRosterValue] = useState([]);

  const handleChangePanel = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const errorHandler = (open, message, severity) => {
    setSnack({
      open: true,
      message: message,
      severity: severity,
    });
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const [projectOptions, setProjectOptions] = useState([]);
  const [project, setProject] = useState({
    ProjectName: "",
    ProjectCode: "",
  });

  const [waveOptions, setWaveOptions] = useState([]);
  const [wave, setWave] = useState({
    Wave: "",
  });

  const [parentClientOptions, setParentClientOptions] = useState([]);
  const [parentClient, setParentClient] = useState({
    ParentClientName: "",
  });

  const [techTypeOptions, setTechTypeOptions] = useState([]);
  const [techType, setTechType] = useState({
    TechTypeName: "",
  });

  useEffect(() => {
    let cancelled = false;

    const loadClassRosters = async () => {
      try {
        if (project || wave) {
          await fetchClassRosters(
            project?.ProjectCode ?? null,
            wave?.Wave ?? null,
          );
        } else {
          await fetchClassRosters();
        }

        if (!cancelled) {
          setRosterValue([]);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch class rosters:", error);
        }
      }
    };

    loadClassRosters();

    return () => {
      cancelled = true;
    };
  }, [project, wave]);

  const fetchClassRosters = async (ProjectCode, wave) => {
    setLoading(true);
    try {
      const response = await getClassRosters(ProjectCode, wave);
      if (response?.data?.message === "Success") {
        setFilterRosterData(response.data.data);
      } else {
        errorHandler(true, "ClassRosters could not be fetched", "error");
      }
    } catch {
      errorHandler(true, "Some internal error occured", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        await fetchData();

        if (!cancelled) {
          applicationUsageLog(
            localStorage.getItem("User"),
            localStorage.getItem("EmailId"),
            "obtinfo",
            "Overture",
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to initialize:", error);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const parentClientResponse = await getAllParentClients();
      if (parentClientResponse?.data?.message === "Success") {
        setParentClientOptions(parentClientResponse?.data?.data);
      } else {
        errorHandler(true, "No Parent Clients Found...", "error");
      }
      const techTypeResponse = await getDistinctTechTypes();
      if (techTypeResponse?.data?.message === "Success") {
        setTechTypeOptions(techTypeResponse?.data?.data);
      } else {
        errorHandler(true, "No Tech Type Found...", "error");
      }
      const classRosterResponse = await getClassRosters();
      if (classRosterResponse?.data?.message === "Success") {
        setFilterRosterData(classRosterResponse?.data?.data);
      } else {
        errorHandler(true, "No Roster Data Found...", "error");
      }
    } catch {
      errorHandler(true, "Some internal error occurred.", "error");
    }
    setLoading(false);
  };

  const handleParentClientChange = async (event, value) => {
    setLoading(true);
    setParentClient(value);
    setProject({
      ProjectName: "",
      ProjectCode: "",
    });
    setWave({
      Wave: "",
    });
    try {
      if (value !== null) {
        const clientResponse = await getAllProjectsByParentClientCode(
          value?.ParentClientCode,
        );
        if (clientResponse?.data?.message === "Success") {
          setProjectOptions(clientResponse?.data?.data);
          setWaveOptions([]);
        } else {
          errorHandler(true, "No data Found..", "error");
        }
      } else {
        setProjectOptions([]);
        setWaveOptions([]);
      }
    } catch {
      errorHandler(true, "Some internal error occurred.", "error");
    }
    setLoading(false);
  };

  const handleProjectChange = async (event, value) => {
    setLoading(true);
    setProject(value);
    setWave({
      Wave: "",
    });

    try {
      if (value !== null) {
        const waveResponse = await getWaveDetails(value?.ProjectCode);
        if (waveResponse?.data?.message === "Success") {
          setWaveOptions(waveResponse?.data?.data);
        } else {
          errorHandler(true, "Waves could not be fetched.", "error");
        }
      } else {
        setWaveOptions([]);
      }
    } catch {
      errorHandler(true, "Some internal error occurred.", "error");
    }
    setLoading(false);
  };

  const handleAgentInputChange = (event) => {
    setAgentSearchValue(event.target.value);
  };

  const handleSearch = async () => {
    const noRoster = !rosterValue || rosterValue.length === 0;
    const noAgentSearch = !agentSearchValue || agentSearchValue.length === 0;
    const noTechType =
      techType?.TechTypeName?.trim() === "" || !techType?.TechTypeName;

    if (noRoster && noTechType && noAgentSearch) {
      errorHandler(
        true,
        "Please select either Class Roster or Tech Type or Agent Search",
        "error",
      );
      return;
    }
    setLoading(true);
    const classRosterIds = rosterValue
      ?.map((item) => item.ClassRosterId)
      .join(",");

    const overtureResponse = await getOvertureData(
      noRoster ? "" : classRosterIds,
      noAgentSearch ? "" : agentSearchValue,
      noTechType ? "" : techType?.TechTypeName,
    );
    if (overtureResponse?.message === "Success") {
      setRows(overtureResponse?.data);
    } else {
      setRows([]);
      errorHandler(true, "No record found.", "error");
    }

    setLoading(false);
    setExpanded("panel2");
  };

  const handleClear = () => {
    setLoading(true);
    setAgentSearchValue("");
    setRows([]);
    setParentClient(null);
    setWave(null);
    setProject(null);
    setRosterValue([]);
    setTechType(null);
    setLoading(false);
  };
  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <ThemeProvider theme={defaultTheme}>
        <Card
          sx={{
            height: "calc(100vh - 80px)",
            width: "calc(100vw - 125px)",
            overflowY: "auto",
          }}
        >
          <Grid container>
            <Grid item xs={12}>
              <Accordion
                expanded={expanded === "panel1"}
                onChange={handleChangePanel("panel1")}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <TextLabel text={"Overture"} fontSize={16}></TextLabel>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container rowGap={0.4}>
                    <Grid item xs={3}>
                      <Grid container>
                        <Grid item xs={12}>
                          <TextLabel text={"Class Roster"}></TextLabel>
                        </Grid>
                        <Grid item xs={10}>
                          <Autocomplete
                            size="small"
                            disablePortal
                            multiple
                            options={filterRosterData}
                            value={rosterValue}
                            getOptionLabel={(option) =>
                              option?.ClassRosterId &&
                              option?.RosterStatus &&
                              option?.Wave
                                ? `${option?.ClassRosterId} - ${option?.RosterStatus} (${option?.Wave})`
                                : ""
                            }
                            onChange={(event, value) => {
                              if (value.length === 0) {
                                setRosterValue([]);
                                setIsMaxRoster(false);
                              } else if (value.length <= 3) {
                                setRosterValue(value);
                                setIsMaxRoster(false);
                              } else {
                                setIsMaxRoster(true);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select RosterID - Status - Wave"
                              />
                            )}
                          />
                          {isMaxRoster && (
                            <Box sx={{ color: "red", marginTop: "10px" }}>
                              You can select up to 3 rosters only.
                            </Box>
                          )}
                        </Grid>
                        <Grid item xs={2} sx={{ pt: 0.2, pl: 1.8 }}>
                          <SwapHorizontalCircleOutlinedIcon
                            sx={{
                              color: "#3299FF",
                              fontSize: 28,
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={3}>
                      <Grid container>
                        <Grid item xs={12}>
                          <TextLabel text={"Parent Client"}></TextLabel>
                        </Grid>
                        <Grid item xs={10}>
                          <Autocomplete
                            size="small"
                            disablePortal
                            options={parentClientOptions}
                            value={parentClient}
                            onChange={handleParentClientChange}
                            getOptionLabel={(option) =>
                              option?.ParentClientName === ""
                                ? ""
                                : `${
                                    option?.ParentClientName +
                                    " (" +
                                    option?.ParentClientCode +
                                    ")"
                                  }`
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select Parent Client"
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={2} sx={{ pt: 0.2, pl: 1.8 }}>
                          <ArrowCircleRightOutlinedIcon
                            sx={{
                              color: "#3299FF",
                              fontSize: 28,
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={3}>
                      <Grid container>
                        <Grid item xs={12}>
                          <TextLabel text={"Project"}></TextLabel>
                        </Grid>
                        <Grid item xs={10}>
                          <Autocomplete
                            size="small"
                            disablePortal
                            blurOnSelect
                            options={projectOptions}
                            value={project}
                            onChange={handleProjectChange}
                            getOptionLabel={(option) =>
                              option?.ProjectName === "" &&
                              option?.ProjectCode === ""
                                ? ""
                                : `${
                                    option?.ProjectName +
                                    " (" +
                                    option?.ProjectCode +
                                    ")"
                                  }`
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select Project"
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={2} sx={{ pt: 0.2, pl: 1.8 }}>
                          <ArrowCircleRightOutlinedIcon
                            sx={{
                              color: "#3299FF",
                              fontSize: 28,
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={3}>
                      <Grid container>
                        <Grid item xs={12}>
                          <TextLabel text={"Wave"}></TextLabel>
                        </Grid>
                        <Grid item xs={10}>
                          <Autocomplete
                            size="small"
                            disablePortal
                            blurOnSelect
                            options={waveOptions}
                            value={wave}
                            onChange={(e, inputValue) => setWave(inputValue)}
                            getOptionLabel={(option) =>
                              option?.Wave === "" ? "" : `${option?.Wave}`
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select Wave"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={3}>
                      <Grid container>
                        <Grid item xs={12}>
                          <TextLabel text={"Tech Type"}></TextLabel>
                        </Grid>
                        <Grid item xs={10}>
                          <Autocomplete
                            size="small"
                            disablePortal
                            blurOnSelect
                            options={techTypeOptions}
                            value={techType}
                            onChange={(event, newValue) =>
                              setTechType(newValue)
                            }
                            getOptionLabel={(option) =>
                              option?.TechTypeName === ""
                                ? ""
                                : option.TechTypeName
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select Tech Type"
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={2} sx={{ pt: 0.2, pl: 1.8 }}>
                          <SwapHorizontalCircleOutlinedIcon
                            sx={{
                              color: "#3299FF",
                              fontSize: 28,
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={3}>
                      <Grid container>
                        <Grid item xs={12}>
                          <TextLabel text={"Agent Search"}></TextLabel>
                        </Grid>
                        <Grid item xs={10}>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Oracle ID(comma-separated)"
                            value={agentSearchValue}
                            onChange={handleAgentInputChange}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                handleSearch();
                              }
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <CustomTooltip title="Search Agent">
                                    <IconButton
                                      id="icon-button-agent-search"
                                      type="button"
                                      onClick={handleSearch}
                                      disabled={!agentSearchValue.trim()}
                                    >
                                      <GridSearchIcon />
                                    </IconButton>
                                  </CustomTooltip>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={3}>
                      <CustomButton
                        variant="contained"
                        sx={{ marginTop: "20px" }}
                        onClick={handleSearch}
                      >
                        Search
                      </CustomButton>
                      <CustomOutlinedButton
                        sx={{ marginTop: "20px", marginLeft: "10px" }}
                        variant="outlined"
                        onClick={handleClear}
                      >
                        Clear
                      </CustomOutlinedButton>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Grid item xs={12} sx={{ display: "flex" }}>
                <Box
                  sx={{
                    flexGrow: 1,
                    marginTop: "1rem",
                    height: "calc(100vh - 80px)",
                    width: "calc(100vw - 125px)",
                    overflow: "auto",
                  }}
                >
                  <DataGrid
                    sx={{
                      height: "100%",
                      "& .MuiDataGrid-columnHeaders": {
                        position: "sticky",
                        zIndex: 1000,
                      },
                      "& .MuiDataGrid-virtualScroller": {
                        marginTop: "0 !important",
                      },
                      "& .MuiDataGrid-main": {
                        overflow: "clip",
                        height: "100%",
                      },
                      "& .MuiDataGrid-sortIcon": {
                        color: "#3299FF",
                      },
                      "& .MuiDataGrid-filterIcon": {
                        color: "#3299FF",
                      },
                      "& .MuiDataGrid-columnHeaderTitle": {
                        fontFamily: "Urbanist, sans-serif",
                        fontWeight: "bold",
                      },
                    }}
                    initialState={{
                      columns: {
                        columnVisibilityModel: {
                          OvertureResultId: false,
                        },
                      },
                    }}
                    columns={columns}
                    rows={rows}
                    getRowId={(row) => row.OvertureResultId}
                    isCellEditable={() => false}
                    pageSize={100}
                    disableSelectionOnClick
                    rowsPerPageOptions={[100]}
                    components={{
                      NoRowsOverlay: CustomNoRowsOverlay,
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <SnackBar snack={snack} snackReset={snackReset} />
        </Card>
      </ThemeProvider>
    </>
  );
}
